import React, { useState } from 'react';
import { ChevronRightIcon, CheckIcon, RefreshIcon } from '@shopify/polaris-icons';
import { Ic, StatusPill } from '../ui';
import { accountState, stateOf, pillTone, systemLabel, formatTime, phase1Steps, retryBlockedReason } from './status';

// Retry and Mark done for a failed step. Retry is shown but disabled, with
// the reason, when re-running would be unsafe (see retryBlockedReason).
function FailedControls({ action, onRetry, onMarkDone, busy }) {
  const blocked = retryBlockedReason(action);
  return (
    <>
      {blocked && <div className="uh-why">{blocked}</div>}
      <div className="uh-decide">
        <div className="uh-actions">
          <button type="button" className={`uh-btn slim${blocked ? '' : ' primary'}`} disabled={!!blocked || busy} onClick={() => onRetry(action.id)}>
            <Ic source={RefreshIcon} sm />{busy ? 'Working…' : 'Retry'}
          </button>
          <button type="button" className={`uh-btn slim${blocked ? ' primary' : ''}`} disabled={busy} onClick={() => onMarkDone(action.id)}>
            <Ic source={CheckIcon} sm />Mark done
          </button>
          {!blocked && <span className="uh-help">Retry runs this step again. Mark done if you finished it yourself.</span>}
        </div>
      </div>
    </>
  );
}

// One action inside a system: its label, state, detail, and — while it
// waits on a person — the approve/ignore choice or the manual "Mark done".
function ActionRow({ action, onResolve, onRetry, onMarkDone, busy, readOnly }) {
  const [selected, setSelected] = useState(null);
  const [tone, label] = stateOf(action.status, action.outcome);
  const candidates = action.match_candidates || [];
  const waiting = action.status === 'requires_feedback' && !readOnly;
  const failed = action.status === 'failure' && !readOnly && onRetry;

  return (
    <div className="uh-act">
      <span className="al">{action.label}</span>
      <StatusPill tone={pillTone(tone)}>{label}</StatusPill>
      {action.detail && <span className="ad">{action.detail}</span>}
      {failed && <FailedControls action={action} onRetry={onRetry} onMarkDone={onMarkDone} busy={busy} />}
      {waiting && (
        <div className="uh-decide">
          {candidates.length > 0 ? (
            <>
              <span className="uh-help">Possible matches. Pick one to approve, or ignore them all.</span>
              {candidates.map((c) => (
                <label className="uh-opt" key={c.external_id}>
                  <input
                    type="radio"
                    name={`candidate-${action.id}`}
                    checked={selected === c.external_id}
                    onChange={() => setSelected(c.external_id)}
                    disabled={busy}
                  />
                  <span>
                    {c.name || '(no name)'}
                    <small>{c.email || '(no email)'} · match score {Math.round(c.score)}</small>
                  </span>
                </label>
              ))}
              <div className="uh-actions">
                <button
                  type="button"
                  className="uh-btn primary slim"
                  disabled={!selected || busy}
                  onClick={() => onResolve(action.id, 'approve', selected)}
                >
                  {busy ? 'Working…' : 'Approve match'}
                </button>
                <button type="button" className="uh-btn slim" disabled={busy} onClick={() => onResolve(action.id, 'ignore', null)}>
                  Ignore, not a match
                </button>
              </div>
            </>
          ) : (
            <>
              <span className="uh-help">No system to check against. This is a manual step: mark it done once you've completed it by hand.</span>
              <div className="uh-actions">
                <button type="button" className="uh-btn primary slim" disabled={busy} onClick={() => onResolve(action.id, 'approve', null)}>
                  <Ic source={CheckIcon} sm />{busy ? 'Working…' : 'Mark done'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Workspace's suspend action stores the full Phase 1 log as its detail.
// The row shows just the steps that ran; the log stays in the expanded view.
function workspaceSummary(account) {
  const phase1 = account.actions.find((a) => (a.detail || '').includes('PHASE 1'));
  if (!phase1) return null;
  const steps = phase1Steps(phase1.detail);
  if (phase1.status === 'failure') {
    return `Failed${steps.length ? ` after: ${steps.join(' · ')}` : ''}. See details.`;
  }
  if (phase1.outcome === 'completed_manually') {
    return `Finished by hand${steps.length ? ` after: ${steps.join(' · ')}` : ''}`;
  }
  const archive = account.actions.find((a) => a.action_key === 'archive_mail_and_drive');
  if (archive && archive.status === 'success') steps.push('Archived');
  return steps.join(' · ') || 'Completed';
}

function summary(account) {
  const waiting = account.actions.filter((a) => a.status === 'requires_feedback');
  if (waiting.length) return waiting.map((a) => a.label).join(' · ');
  const ws = workspaceSummary(account);
  if (ws) return ws;
  const failed = account.actions.find((a) => a.status === 'failure');
  if (failed) return failed.detail || failed.label;
  if (account.detail) return account.detail;
  const withDetail = account.actions.find((a) => a.detail);
  return withDetail ? withDetail.detail : account.actions.map((a) => a.label).join(' · ');
}

function lastUpdated(account) {
  const stamps = [account.updated_at, ...account.actions.map((a) => a.updated_at)].filter(Boolean);
  if (!stamps.length) return '';
  return stamps.reduce((a, b) => (new Date(a) > new Date(b) ? a : b));
}

// Every system in a run: one row each, expandable to its actions. Rows
// that need a person or failed start open. readOnly hides the decision
// controls (CSV-only runs, which have nothing to resolve).
function SystemsTable({ run, onResolve, onRetry, onMarkDone, busyActionId, readOnly = false }) {
  const [open, setOpen] = useState(() =>
    Object.fromEntries(run.accounts.map((a) => [a.id ?? a.system, a.status === 'requires_feedback' || a.status === 'failure'])));
  const keyOf = (a) => a.id ?? a.system;
  const allOpen = run.accounts.every((a) => open[keyOf(a)]);
  const setAll = (v) => setOpen(Object.fromEntries(run.accounts.map((a) => [keyOf(a), v])));

  return (
    <div className="uh-card">
      <div className="uh-card-h">
        <h2 className="uh-h2">Systems</h2>
        <span className="uh-tcount">{run.accounts.length}</span>
        <span className="uh-sp" />
        <button type="button" className="uh-btn slim" onClick={() => setAll(!allOpen)}>
          {allOpen ? 'Collapse all' : 'Expand all'}
        </button>
      </div>
      <div className="uh-tscroll">
        <table className="uh-tbl">
          <thead>
            <tr><th style={{ width: 36 }} /><th>System</th><th>Status</th><th>Result</th><th>Updated</th></tr>
          </thead>
          <tbody>
            {run.accounts.map((acc) => {
              const k = keyOf(acc);
              const [tone, label] = accountState(acc);
              const isOpen = !!open[k];
              return (
                <React.Fragment key={k}>
                  <tr>
                    <td>
                      <button
                        type="button"
                        className="uh-caret"
                        aria-expanded={isOpen}
                        aria-label={`Show steps for ${systemLabel(acc.system)}`}
                        onClick={() => setOpen((o) => ({ ...o, [k]: !o[k] }))}
                      >
                        <Ic source={ChevronRightIcon} sm />
                      </button>
                    </td>
                    <td className="nm">{systemLabel(acc.system)}</td>
                    <td><StatusPill tone={pillTone(tone)}>{label}</StatusPill></td>
                    <td>{summary(acc)}</td>
                    <td className="uh-muted uh-num" style={{ whiteSpace: 'nowrap' }}>{formatTime(lastUpdated(acc))}</td>
                  </tr>
                  {isOpen && (
                    <tr className="uh-x">
                      <td colSpan={5}>
                        <div className="uh-acts">
                          {acc.actions.map((a) => (
                            <ActionRow
                              key={a.id ?? a.action_key}
                              action={a}
                              onResolve={onResolve}
                              onRetry={onRetry}
                              onMarkDone={onMarkDone}
                              busy={busyActionId === a.id}
                              readOnly={readOnly}
                            />
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SystemsTable;
